/**
 * Account settings — the editable bits of a client's profile.
 *
 *   • Display name (your display name) + first/last name. Onboarding may not
 *     have captured these, so this is where a new client fills them in.
 *   • Share health data with coach — opt-in (default off). When on, your coach can
 *     see the synced Apple Health / Health Connect metrics (steps, sleep, heart
 *     rate, body composition) over time. Off = only the data you log for
 *     coaching (measurements, workouts) is visible.
 *
 * Saves via PATCH /api/me, then refresh()es the auth user so the rest of the
 * app reflects the change immediately.
 */

import { AvatarUploader } from '@/components/AvatarUploader';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { DeleteAccountCard } from '@/components/DeleteAccountCard';
import { HealthSyncNotice } from '@/components/HealthSyncNotice';
import type { User } from '@fitplatform/shared';

export default function Settings() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [shareHealth, setShareHealth] = useState(false);
  // "Email me when I get a new message" — users.notification_enabled, saved
  // immediately via PUT /api/user/settings.
  const [emailNotify, setEmailNotify] = useState(true);

  useEffect(() => {
    api
      .get<{ notification_enabled: boolean }>('/api/user/settings')
      .then((s) => setEmailNotify(s.notification_enabled))
      .catch(() => {});
  }, []);

  function toggleEmailNotify() {
    const next = !emailNotify;
    setEmailNotify(next);
    api.put('/api/user/settings', { notification_enabled: next }).catch(() => setEmailNotify(!next));
  }
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Seed the form from the current user once it's loaded.
  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name ?? '');
    setFirstName(user.first_name ?? '');
    setLastName(user.last_name ?? '');
    setShareHealth(user.share_health_with_coach === 1);
  }, [user]);

  async function save() {
    setSaving(true);
    try {
      await api.patch<{ user: User }>('/api/me', {
        display_name: displayName.trim() || null,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        share_health_with_coach: shareHealth ? 1 : 0,
      });
      await refresh();
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader title="Settings" />
      <div className="px-5 flex flex-col gap-4">
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center gap-1.5 text-sm self-start"
          style={{ color: 'var(--gold-muted)' }}
        >
          <ArrowLeft size={16} /> Profile
        </button>

        <AvatarUploader />

        {/* Name */}
        <div className="card flex flex-col gap-3">
          <p className="text-white font-semibold">Your name</p>
          <label className="flex flex-col gap-1">
            <span className="text-xs" style={{ color: 'var(--gray-400)' }}>
              What should we call you?
            </span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Vicky"
              className="input"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs" style={{ color: 'var(--gray-400)' }}>First name</span>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="input" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs" style={{ color: 'var(--gray-400)' }}>Last name</span>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="input" />
            </label>
          </div>
        </div>

        {/* Health sharing */}
        <div className="card flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-white font-semibold">Share health data with coach</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--gray-400)' }}>
                Let your coach see your synced steps, sleep, heart rate, and body composition over time.
              </p>
            </div>
            <button
              role="switch"
              aria-checked={shareHealth}
              onClick={() => setShareHealth((v) => !v)}
              className="shrink-0 w-12 h-7 rounded-full transition-colors relative"
              style={{ background: shareHealth ? 'var(--gold)' : 'rgba(255,255,255,0.15)' }}
            >
              <span
                className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all"
                style={{ left: shareHealth ? '26px' : '4px' }}
              />
            </button>
          </div>
        </div>

        {/* Message emails */}
        <div className="card flex items-center justify-between gap-4">
          <div>
            <p className="text-white font-semibold">Email me about new messages</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--gray-400)' }}>
              Get an email when someone sends you a message in the app. The email never includes the message itself.
            </p>
          </div>
          <button
            role="switch"
            aria-checked={emailNotify}
            onClick={toggleEmailNotify}
            className="shrink-0 w-12 h-7 rounded-full transition-colors relative"
            style={{ background: emailNotify ? 'var(--gold)' : 'rgba(255,255,255,0.15)' }}
          >
            <span
              className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all"
              style={{ left: emailNotify ? '26px' : '4px' }}
            />
          </button>
        </div>

        {/* Where synced health data comes from (phone app; not this website). */}
        <HealthSyncNotice showMeasurementsLink={user?.role === 'user'} />

        <button
          onClick={save}
          disabled={saving}
          className="card flex items-center justify-center py-3.5 cursor-pointer font-semibold disabled:opacity-50"
          style={{ background: 'var(--gold)', color: 'var(--navy)' }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {savedAt && (
          <p className="text-center text-sm" style={{ color: 'var(--success, #22C55E)' }}>
            Saved.
          </p>
        )}

        {user?.role === 'user' && <DeleteAccountCard />}
      </div>
    </>
  );
}
